import { useEffect, useRef, useState } from "react";
import { RotateCcw, RotateCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { loadPdfDocument } from "./utils";
import { renderPageThumbnail } from "./pdfThumbnails";
import { parseMultiRange } from "./operations";
import { toggleRemoved, turnPage, normalizeTurn, type PageEdits } from "./mergePagePlan";

type PdfDoc = Awaited<ReturnType<typeof loadPdfDocument>>;

/**
 * "Choose pages" panel for one file: a grid of small page thumbnails, drawn only when they scroll
 * into view (a 300-page file opens at once and renders just what is on screen). Each page can be
 * turned off and rotated. The file is opened in pdf.js only while this panel is open.
 */
export function MergePagePicker({ file, pageCount, rangeText, edits, onChange, onClose }: {
  file: File;
  pageCount: number;
  rangeText: string;
  edits: PageEdits;
  onChange: (next: PageEdits) => void;
  onClose: () => void;
}) {
  const [doc, setDoc] = useState<PdfDoc | null>(null);
  const [failed, setFailed] = useState(false);
  // Renders go through one queue so scrolling a long file never starts dozens of pdf.js renders at once.
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const docRef = useRef<PdfDoc | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDoc(null); setFailed(false);
    loadPdfDocument(file).then((d) => {
      if (cancelled) { void d.loadingTask.destroy(); return; }
      docRef.current = d;
      setDoc(d);
    }).catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      const d = docRef.current;
      docRef.current = null;
      if (d) void d.loadingTask.destroy();
    };
  }, [file]);

  const inRange = (() => {
    const t = rangeText.trim();
    if (!t) return null;
    const r = parseMultiRange(t, pageCount);
    return "error" in r ? null : new Set(r.pages.map((i) => i + 1));
  })();
  const removed = new Set(edits.removed ?? []);
  const kept = Array.from({ length: pageCount }, (_, i) => i + 1).filter((n) => !removed.has(n) && (!inRange || inRange.has(n))).length;

  const render = (n: number) => {
    const run = queue.current.then(async () => {
      const d = docRef.current;
      if (!d) return null;
      try { return await renderPageThumbnail(d, n, 90); } catch { return null; }
    });
    queue.current = run;
    return run;
  };

  return (
    <div className="mt-3 rounded-lg border border-border p-3" data-testid="merge-page-picker">
      <div className="flex items-start gap-2">
        <p className="text-sm flex-1 min-w-0">
          <span className="font-medium break-words">{file.name}</span>
          <span className="text-muted-foreground"> — {kept} of {pageCount} {pageCount === 1 ? "page" : "pages"} will be used. Tap a page to leave it out, use the arrows to rotate it.</span>
        </p>
        <Button type="button" variant="outline" size="sm" className="h-8 shrink-0" onClick={onClose}>Done</Button>
      </div>
      {inRange && <p className="text-xs text-muted-foreground mt-1">Pages outside "{rangeText.trim()}" are already left out by the page range.</p>}
      {failed ? (
        <p className="text-sm text-muted-foreground mt-3">Page previews aren't available for this file. You can still use the page range.</p>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2 max-h-80 overflow-y-auto" data-testid="merge-page-grid">
          {doc && Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
            <PageTile
              key={n}
              n={n}
              render={render}
              off={removed.has(n) || (!!inRange && !inRange.has(n))}
              outside={!!inRange && !inRange.has(n)}
              turn={normalizeTurn(edits.rotations?.[n] ?? 0)}
              onToggle={() => onChange({ ...edits, removed: toggleRemoved(edits.removed, n) })}
              onTurn={(d) => onChange({ ...edits, rotations: turnPage(edits.rotations, n, d) })}
            />
          ))}
          {!doc && <p className="text-sm text-muted-foreground">Opening pages…</p>}
        </div>
      )}
    </div>
  );
}

function PageTile({ n, render, off, outside, turn, onToggle, onTurn }: {
  n: number;
  render: (n: number) => Promise<string | null>;
  off: boolean;
  outside: boolean;
  turn: number;
  onToggle: () => void;
  onTurn: (d: 90 | -90) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [url, setUrl] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const start = () => {
      if (started.current) return;
      started.current = true;
      void render(n).then((u) => setUrl(u));
    };
    if (typeof IntersectionObserver === "undefined") { start(); return; }
    const io = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) { start(); io.disconnect(); } }, { rootMargin: "120px" });
    io.observe(el);
    return () => io.disconnect();
  }, [n, render]);

  return (
    <div ref={ref} className="w-[88px]" data-testid="merge-page-tile" data-off={off ? "true" : "false"}>
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={!off}
        aria-label={`Page ${n}${outside ? ", outside the page range" : off ? ", left out" : ", included"}${turn ? `, rotated ${turn} degrees` : ""}`}
        disabled={outside}
        className={`relative flex h-[112px] w-[88px] items-center justify-center overflow-hidden rounded border bg-muted/40 ${off ? "opacity-40 border-dashed" : "border-border"}`}
      >
        {url ? (
          <img src={url} alt="" className="max-h-[104px] max-w-[80px] object-contain transition-transform" style={{ transform: `rotate(${turn}deg)`, ...(turn % 180 ? { maxHeight: 80, maxWidth: 104 } : {}) }} draggable={false} />
        ) : (
          <span className="text-xs text-muted-foreground">{n}</span>
        )}
        {off && !outside && <X className="absolute h-8 w-8 text-destructive" aria-hidden="true" />}
      </button>
      <div className="mt-1 flex items-center justify-between">
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={off} onClick={() => onTurn(-90)} aria-label={`Rotate page ${n} left`} title="Rotate left"><RotateCcw className="h-4 w-4" /></Button>
        <span className="text-xs text-muted-foreground">{n}</span>
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={off} onClick={() => onTurn(90)} aria-label={`Rotate page ${n} right`} title="Rotate right"><RotateCw className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}
